import { usePerms } from 'contexts/PermsContext';
import { useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { usePostAlertMutation } from 'services/alerts/alerts.api';
import { useGetJurisdictionsQuery } from 'services/geography/geography.api';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import '../../assets/alertCreator.scss';
import AlertLocationSearch from '../../features/AlertLocationSearch/index.web';
import {
  selectAlertMode,
  selectRescAlertDraft,
  selectTimeInputMode,
} from '../../state/selectors/alertCreatorSelectors';
import {
  clearCoordinates,
  removeLastCoordinate,
  setAlertMode,
  setRescAlertDraft,
} from '../../state/slices/alertCreatorSlice';
import {
  ALERT_MODES,
  createInitialRescAlertDraft,
  validateRescAlertDraft,
} from '../../utils/alertCreator.utils';
import AlertModeSelector from '../AlertModeSelector/AlertModeSelector';
import IpawsAlertFields from '../IpawsAlertFields/IpawsAlertFields';
import {
  ALERT_CREATOR_ACCESS_LEVELS,
  FEMA_PERMISSIONS,
} from '../IpawsAlertFields/config/Ipaws.config';
import RescAlertFields from '../RescAlertFields/RescAlertFields';

/**
 * Shows the shared Alert Creator form and chooses the fields for the current alert type.
 * The widget and the full Alert Creator both offer IPAWS to an operator who holds the FEMA right.
 */
const AlertCreatorFormPanel = ({ widget }) => {
  const [postAlert] = usePostAlertMutation();
  const { data: jurisdictions = [] } = useGetJurisdictionsQuery();

  const alertMode = useSelector(selectAlertMode);
  const rescAlertDraft = useSelector(selectRescAlertDraft);
  const timeInputMode = useSelector(selectTimeInputMode);

  const dispatch = useDispatch();

  const perms = usePerms();
  // Absent, not disabled: without CreateFemaAlert the IPAWS mode does not exist on this screen.
  const canCreateFema = perms.testPermission(ALERT_CREATOR_ACCESS_LEVELS, [
    FEMA_PERMISSIONS.create,
  ]);

  const isIpaws = canCreateFema && alertMode === ALERT_MODES.IPAWS;
  const isRescAlert = !isIpaws;

  // A draft left in IPAWS mode by a user who has since lost the right falls back to RescAlert
  // instead of showing fields that cannot be sent.
  useEffect(() => {
    if (!canCreateFema && alertMode !== ALERT_MODES.RESCALERT) {
      dispatch(setAlertMode(ALERT_MODES.RESCALERT));
    }
  }, [alertMode, canCreateFema, dispatch]);

  const isFormValid =
    isRescAlert && validateRescAlertDraft(rescAlertDraft, jurisdictions).length === 0;

  const handleSubmit = async () => {
    if (!isRescAlert) return;

    const errors = validateRescAlertDraft(rescAlertDraft, jurisdictions);

    if (errors.length) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    try {
      const response = await postAlert(rescAlertDraft).unwrap();

      notifySuccessToast('Alert', { isAddOperation: true }, { successPayload: response });

      dispatch(setRescAlertDraft(createInitialRescAlertDraft(timeInputMode)));
    } catch (error) {
      notifyErrorToast('Alert', { isAddOperation: true }, { errorPayload: error });
    }
  };

  return (
    <Form
      className={`alert-creator-form flex-grow-1 d-flex flex-column gap-3 min-h-0 pe-1 ${
        widget ? 'alert-creator-form-widget' : ''
      }`}
    >
      {canCreateFema && <AlertModeSelector />}

      <section className="alert-creator-section alert-creator-location-section border rounded-3 p-3 shadow-sm">
        <h2 className="alert-creator-section-title d-flex align-items-center">
          Find a map location
        </h2>
        <AlertLocationSearch widget={widget} />
      </section>

      <div className="alert-creator-fields d-flex flex-column gap-3">
        {isRescAlert ? <RescAlertFields /> : <IpawsAlertFields />}
      </div>

      {/* These buttons change RescAlert's point list. IPAWS has separate multi-shape controls. */}
      {isRescAlert && (
        <footer
          className={`alert-creator-actions d-flex flex-column ${
            !widget ? 'flex-sm-row' : ''
          } gap-2 mt-1`}
        >
          <Button
            type="button"
            variant="secondary"
            className="alert-creator-action alert-creator-action-remove d-flex w-100 align-items-center justify-content-center rounded-3"
            onClick={() => dispatch(removeLastCoordinate())}
          >
            Remove Last Coordinate
          </Button>

          <Button
            type="button"
            variant="secondary"
            className="alert-creator-action alert-creator-action-clear d-flex w-100 align-items-center justify-content-center rounded-3"
            onClick={() => dispatch(clearCoordinates())}
          >
            Clear Coordinates
          </Button>

          <Button
            type="button"
            variant="primary"
            className="alert-creator-action alert-creator-submit d-flex w-100 align-items-center justify-content-center rounded-3 shadow-sm"
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            Create Alert
          </Button>
        </footer>
      )}
    </Form>
  );
};

export default AlertCreatorFormPanel;
